// A secret shown once leaves the page when the page is left (#929).
//
// `pagehide` is the last event a page sees before the back-forward cache freezes
// it; a page restored from there comes back exactly as it was frozen, with no
// render and no request. So the secret is dropped on `pagehide`, and the drop is
// committed there and then (`flushSync`): a render scheduled for later would run
// only after the page came back, with the secret already on screen.
//
// The listener is attached and removed in a layout effect, in the commit that shows
// the secret or takes its step away. A passive effect runs in a later task, so a page
// left in between was not heard by the step on screen and was heard by the step just
// gone (#1022).
//
// `visibilitychange` is not used: a person who switches tab and back is still on
// the page, and the secret they have not copied yet must still be there.

import { useLayoutEffect, useRef } from "react";
import { flushSync } from "react-dom";

/** Call `drop` when the page is left, committing whatever it changes at once. */
export function useDropOnPageHide(drop: () => void): void {
  const latest = useRef(drop);
  latest.current = drop;
  useLayoutEffect(() => {
    const onHide = () => flushSync(() => latest.current());
    window.addEventListener("pagehide", onHide);
    return () => window.removeEventListener("pagehide", onHide);
  }, []);
}
